import { CSSProperties, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import { REFERRAL_SOURCES } from "../referralSources.js";
import { contactedText } from "../contacted.js";
import { ClientDetail } from "./Clients.js";
import { ContactButtons } from "../components/ContactButtons.js";
import { HorizontalBars, StatCard, VerticalBars } from "../components/Charts.js";
import { useIsModern } from "../theme.js";
import {
  ActiveRemindersModern,
  DoneRemindersModern,
  RemindersStatsModern,
  SnoozedRemindersModern,
} from "./Reminders.modern.js";

export interface ClientLite {
  id: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  instagram: string | null;
  instagramActive: boolean | null;
  telegramUsername: string | null;
  viberPhone: string | null;
  preferredChannel: string | null;
}

export interface ReminderRow {
  id: string;
  kind: ReminderKind;
  dueAt: string;
  createdAt: string;
  scriptVariants: string[];
  status: string;
  client: ClientLite;
  /** For followup_after_visit: why it fired. */
  followupReason?: "first_visit" | "new_master" | null;
  /** For kind=manual only. */
  manualTitle?: string | null;
  manualPriority?: "super_urgent" | "urgent" | "normal" | null;
  /** Coach-note from owner/dev when they bounced the task back. */
  reopenNote?: string | null;
  reopenedAt?: string | null;
  reopenedByTelegramId?: string | null;
  /** Resolved name of who bounced the task back. */
  reopenedBy?: { telegramId: string; name: string | null } | null;
  /** Коли адмінка позначила «написала, чекаю відповіді». */
  contactedAt?: string | null;
  /** Who owns this task (null = legacy). Owner+primary see all; shadow sees only own. */
  assignedAdminId?: string | null;
  assignedAdmin?: { id: string; name: string | null } | null;
}

export type ReminderKind =
  | "followup_after_visit"
  | "ask_referral_source"
  | "rebook_1m"
  | "rebook_3m"
  | "rebook_6m"
  | "rebook_1y"
  | "negative_review_master"
  | "manual";

type SectionGroup = "superurgent" | "urgent" | "later";

export interface SectionDef {
  kind: ReminderKind;
  emoji: string;
  title: string;
  hint: string;
  /** Whether discount placeholder is meaningful for this kind. */
  hasDiscount: boolean;
  group: SectionGroup;
}

export const SECTIONS: SectionDef[] = [
  // ---- 🚨 СУПЕР ТЕРМІНОВО — client hit exactly the 1-month mark ----
  {
    kind: "rebook_1m",
    emoji: "🚨",
    title: "Рівно місяць без візиту",
    hint: "Клієнтка не була місяць — терміново запроси повернутись",
    hasDiscount: false,
    group: "superurgent",
  },
  // ---- 🔴 ТЕРМІНОВІ — first-priority tasks for the admin ----
  {
    kind: "followup_after_visit",
    emoji: "🌸",
    title: "Спитати як пройшов візит",
    hint: "Новенька клієнтка або вперше у цього майстра — спитай чи все ок",
    hasDiscount: false,
    group: "urgent",
  },
  {
    kind: "ask_referral_source",
    emoji: "📍",
    title: "Спитати звідки дізналась",
    hint: "Новенькі клієнтки — спитай як про нас почули",
    hasDiscount: false,
    group: "urgent",
  },
  // ---- Решта — sorted reminders ----
  {
    kind: "rebook_3m",
    emoji: "💸",
    title: "Знижка — 3 міс без візиту",
    hint: "Постав суму знижки і запропонуй",
    hasDiscount: true,
    group: "later",
  },
  {
    kind: "rebook_6m",
    emoji: "💸",
    title: "Знижка — півроку без візиту",
    hint: "Постав суму знижки і запропонуй",
    hasDiscount: true,
    group: "later",
  },
  {
    kind: "rebook_1y",
    emoji: "💸",
    title: "Нагадати про себе (рік+)",
    hint: "Запропонуй знижку щоб повернути",
    hasDiscount: true,
    group: "later",
  },
];

const INITIAL_VISIBLE = 8;

type RemindersView = "active" | "snoozed" | "done" | "stats";

const KIND_SHORT: Record<string, string> = {
  followup_after_visit: "🌸 Як пройшло",
  ask_referral_source: "📍 Звідки про нас",
  rebook_1m: "📅 1 міс",
  rebook_3m: "💸 3 міс",
  rebook_6m: "💸 6 міс",
  rebook_1y: "💸 1 рік+",
  negative_review_master: "⚠️ Скарга",
  manual: "📝 Ручна",
};
const OUTCOME_SHORT: Record<string, string> = {
  booked: "✅ Записалась",
  declined: "❌ Відмовилась",
  not_replied: "🤷 Не відповіла",
  review_positive: "👍 Гарний відгук",
  review_negative: "👎 Поганий відгук",
  review_mixed: "🟡 50/50",
};

// Компактна картка «Моя зарплата» для адмінки (Таня) — self-scoped, цикл
// 23→23. Показує підсумок + розбивку зі штрафами. Fail-silent.
function salaryUah(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
function fmtCycle(startIso: string, endIso: string): string {
  const f = (iso: string) =>
    new Intl.DateTimeFormat("uk-UA", {
      timeZone: "Europe/Kyiv",
      day: "2-digit",
      month: "2-digit",
    }).format(new Date(iso));
  return `${f(startIso)} → ${f(endIso)}`;
}
function penaltyDT(iso: string): string {
  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "Europe/Kyiv",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}
function MySalaryCard() {
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.mySalary().then(setData).catch(() => setFailed(true));
  }, []);
  if (failed || !data) return null;
  const penalties = (data.tonusPenalty ?? 0) + (data.ownerPenalty ?? 0);
  const Row = ({
    label,
    value,
    neg,
    bold,
  }: {
    label: string;
    value: number;
    neg?: boolean;
    bold?: boolean;
  }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        fontWeight: bold ? 800 : 500,
      }}
    >
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span
        style={{
          whiteSpace: "nowrap",
          color: neg ? "#dc2626" : bold ? "var(--rose-700, #be185d)" : "inherit",
        }}
      >
        {neg ? "−" : ""}
        {salaryUah(Math.abs(value))} ₴
      </span>
    </div>
  );
  return (
    <div
      style={{
        border: "1px solid rgba(236,72,153,0.30)",
        background: "rgba(236,72,153,0.06)",
        borderRadius: 14,
        padding: "12px 14px",
        marginBottom: 12,
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: 0,
          cursor: "pointer",
          fontFamily: "inherit",
          color: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 14 }}>
          {open ? "▾" : "▸"} 💰 Моя зарплата
          <span style={{ opacity: 0.6, fontWeight: 500, fontSize: 12 }}>
            {" · "}
            {fmtCycle(data.monthStart, data.monthEnd)}
          </span>
        </span>
        <span style={{ fontWeight: 800, fontSize: 15, whiteSpace: "nowrap" }}>
          {salaryUah(data.finalPay)} ₴
        </span>
      </button>
      {open && (
        <div
          style={{
            marginTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 13,
          }}
        >
          <Row label="Базова ставка" value={data.baseSalary} />
          <Row
            label={`Задачі (${data.totalClosed} закрито)`}
            value={data.earnedFromTasks}
          />
          {data.conversionBonus > 0 && (
            <Row
              label={`Бонус за конверсію (${data.conversionRate}%)`}
              value={data.conversionBonus}
            />
          )}
          {penalties > 0 && (
            <>
              <Row label="Штрафи за простій" value={penalties} neg />
              <div
                style={{
                  paddingLeft: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  marginTop: -2,
                }}
              >
                {(data.penaltyDetails ?? []).map((p: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      fontSize: 11.5,
                      opacity: 0.85,
                    }}
                  >
                    <span>
                      {penaltyDT(p.at)} ·{" "}
                      {p.kind === "warn" ? "простій 4+ год" : "8+ год · звіт Любі"}
                      {p.idleHours != null
                        ? ` · ~${Math.round(p.idleHours)} год без задач`
                        : ""}
                    </span>
                    <span style={{ color: "#dc2626", whiteSpace: "nowrap" }}>
                      −{salaryUah(p.amount)} ₴
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
          <div
            style={{
              height: 1,
              background: "rgba(127,127,127,0.2)",
              margin: "3px 0",
            }}
          />
          <Row label="Разом до виплати" value={data.finalPay} bold />
          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>
            Виплата 23-го числа. Період рахується з 23-го по 23-те (не
            календарний місяць).
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Помилка дії над задачею. REMINDER_GONE від бекенду — це не поломка: задачі
 * вже немає (нічний cron о 23:30 перебудовує чергу з новими id, або клієнтка
 * перезаписалась і rebook прибрався в реальному часі). Показуємо людський
 * текст і оновлюємо список, а не сирий 500.
 */
function handleReminderActionError(e: unknown, reload?: () => void): void {
  const msg = String(e);
  if (msg.includes("REMINDER_GONE")) {
    reload?.();
    alert(
      "Ця задача вже закрита або зникла зі списку — оновлюю список.\n\n" +
        "Так буває якщо клієнтка щойно записалась, або якщо вкладка була " +
        "відкрита через ніч (о 23:30 чергу перебудовує).",
    );
    return;
  }
  alert(msg);
}

export function RemindersTab() {
  const [view, setView] = useState<RemindersView>("active");
  const [isOwner, setIsOwner] = useState(false);
  const modern = useIsModern();

  useEffect(() => {
    api.me().then((m) => setIsOwner(m.isOwner)).catch(() => {});
  }, []);

  // Sub-tabs live in ONE place — always rendered at the top of the tab —
  // so they don't blink/disappear when switching between sub-views.
  // Sub-tabs (включно зі Статистикою+зп) — owner-only.
  return (
    <div>
      {isOwner && modern && (
        <ModernSubTabsRow view={view} setView={setView} />
      )}
      {!modern && isOwner && (
        <div
          className="tabs"
          style={{ marginBottom: 8, flexWrap: "wrap" }}
        >
          <button
            className={view === "active" ? "active" : ""}
            onClick={() => setView("active")}
          >
            Активні
          </button>
          <button
            className={view === "snoozed" ? "active" : ""}
            onClick={() => setView("snoozed")}
          >
            Відкладені
          </button>
          <button
            className={view === "done" ? "active" : ""}
            onClick={() => setView("done")}
          >
            Виконано
          </button>
          <button
            className={view === "stats" ? "active" : ""}
            onClick={() => setView("stats")}
          >
            Статистика
          </button>
        </div>
      )}
      {!isOwner && <MySalaryCard />}
      {modern && view === "active" && (
        <ActiveRemindersModern
          isOwner={isOwner}
          subView={view}
          onSubViewChange={(v) => setView(v as RemindersView)}
        />
      )}
      {!modern && view === "active" && <ActiveReminders isOwner={isOwner} />}
      {modern && view === "snoozed" && <SnoozedRemindersModern />}
      {!modern && view === "snoozed" && <SnoozedReminders />}
      {modern && view === "done" && <DoneRemindersModern />}
      {!modern && view === "done" && <DoneReminders />}
      {modern && view === "stats" && <RemindersStatsModern />}
      {!modern && view === "stats" && <RemindersStats />}
    </div>
  );
}

function ModernSubTabsRow({
  view,
  setView,
}: {
  view: RemindersView;
  setView: (v: RemindersView) => void;
}) {
  return (
    <div className="nz-segwrap" style={{ marginTop: 6, marginBottom: 14 }}>
      <button className="nz-seg" data-on={view === "active"} onClick={() => setView("active")}>
        Активні
      </button>
      <button className="nz-seg" data-on={view === "snoozed"} onClick={() => setView("snoozed")}>
        Відкладені
      </button>
      <button className="nz-seg" data-on={view === "done"} onClick={() => setView("done")}>
        Виконано
      </button>
      <button className="nz-seg" data-on={view === "stats"} onClick={() => setView("stats")}>
        Статистика
      </button>
    </div>
  );
}

function DoneReminders() {
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  // Inline reopen form: which row is open + the note being typed.
  const [reopenForId, setReopenForId] = useState<string | null>(null);
  const [reopenNote, setReopenNote] = useState("");
  const [reopenSaving, setReopenSaving] = useState(false);
  const [shown, setShown] = useState(10);

  function load() {
    api
      .doneReminders()
      .then((d) => setItems(d as any[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(() => {
    load();
  }, []);

  async function reopen(id: string) {
    if (reopenSaving) return;
    setReopenSaving(true);
    try {
      await api.reopenReminder(id, reopenNote);
      setReopenForId(null);
      setReopenNote("");
      load();
    } catch (e) {
      handleReminderActionError(e, load);
    } finally {
      setReopenSaving(false);
    }
  }

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!items) return <div className="hint">Завантаження...</div>;
  if (items.length === 0)
    return (
      <div className="card">
        <p style={{ margin: 0 }}>Поки нічого не закрито.</p>
      </div>
    );

  // Items are already in newest-first order from the server. Slice to the
  // current window, THEN group by day — so each "Load more" extends within
  // the same day-group structure.
  const slice = items.slice(0, shown);
  const groups = new Map<string, any[]>();
  for (const r of slice) {
    const d = new Date(r.actedAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  // When a client profile is open, hide the list with display:none INSTEAD
  // of unmounting. That way scroll position, expanded shown-count, and any
  // in-progress reopen textarea all survive — owner returns exactly where
  // she left off.
  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
      <div style={{ display: selectedClientId ? "none" : "block" }}>
      <p className="hint" style={{ marginBottom: 8 }}>
        Закритих нагадувань: показую {slice.length} з {items.length}. Адмін про цю вкладку не знає.
      </p>
      {[...groups.entries()].map(([day, rows]) => (
        <div key={day} style={{ marginBottom: 12 }}>
          <div className="hint" style={{ margin: "4px 0 6px 4px", fontWeight: 600 }}>
            {prettyDay(day)} · {rows.length}
          </div>
          {rows.map((r) => {
            const t = new Date(r.actedAt);
            const time = `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
            const name = `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
            const outcomeLabel =
              r.status === "ignored"
                ? "⏭ Пропущено"
                : r.outcome
                  ? OUTCOME_SHORT[r.outcome] ?? r.outcome
                  : "—";
            return (
              <div
                key={r.id}
                className="card"
                style={{ padding: 10, marginBottom: 6 }}
              >
                <div
                  onClick={() => setSelectedClientId(r.client.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="row">
                    <span style={{ fontWeight: 600, minWidth: 50 }}>{time}</span>
                    <span style={{ flex: 1 }}>{name}</span>
                    <span style={{ fontSize: 12 }}>
                      {KIND_SHORT[r.kind] ?? r.kind}
                    </span>
                  </div>
                  <div className="hint" style={{ fontSize: 12, marginTop: 2 }}>
                    → <b>{outcomeLabel}</b>
                    {r.closedBy?.name && (
                      <span style={closerPill}>👤 {r.closedBy.name}</span>
                    )}
                    {r.client.instagram ? ` · @${r.client.instagram}` : ""}
                    <span style={{ float: "right" }}>›</span>
                  </div>
                </div>
                {reopenForId === r.id ? (
                  <div style={{ marginTop: 8 }}>
                    <textarea
                      autoFocus
                      placeholder="✍️ Напиши Тані, де натупила. Напр. «не записала, хоча клієнтка погодилась» / «коментар не дослівний»"
                      value={reopenNote}
                      onChange={(e) => setReopenNote(e.target.value)}
                      rows={3}
                      style={{ ...textareaStyle, fontSize: 14 }}
                    />
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button
                        onClick={() => reopen(r.id)}
                        disabled={reopenSaving || reopenNote.trim().length === 0}
                        style={{
                          ...smallBtn("var(--button)", "var(--button-text)"),
                          opacity:
                            reopenSaving || reopenNote.trim().length === 0
                              ? 0.5
                              : 1,
                        }}
                      >
                        {reopenSaving
                          ? "Повертаю..."
                          : "↩️ Повернути з коментарем"}
                      </button>
                      <button
                        onClick={() => {
                          setReopenForId(null);
                          setReopenNote("");
                        }}
                        style={smallBtn()}
                      >
                        Скасувати
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setReopenForId(r.id);
                      setReopenNote("");
                    }}
                    style={reopenBtn}
                  >
                    ↩️ Повернути в задачі
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {shown < items.length && (
        <button
          onClick={() => setShown(shown + 10)}
          style={{
            width: "100%",
            padding: "10px 14px",
            marginTop: 6,
            borderRadius: 10,
            border: "1px dashed rgba(127,127,127,0.4)",
            background: "transparent",
            color: "var(--text)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ⬇ Показати ще (+{Math.min(10, items.length - shown)})
        </button>
      )}
      </div>
    </>
  );
}

// ---- Відкладені (owner / developer only) ----

function SnoozedReminders() {
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [shown, setShown] = useState(10);

  function load() {
    api
      .snoozedReminders()
      .then((d) => setItems(d as any[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(() => {
    load();
  }, []);

  async function unsnooze(id: string, name: string) {
    if (!window.confirm(`Повернути «${name}» в активні задачі?`)) return;
    try {
      await api.reopenReminder(id);
      load();
    } catch (e) {
      handleReminderActionError(e, load);
    }
  }

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!items) return <div className="hint">Завантаження...</div>;
  if (items.length === 0)
    return (
      <div className="card">
        <p style={{ margin: 0 }}>Відкладених нагадувань нема.</p>
      </div>
    );

  const slice = items.slice(0, shown);
  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
    <div style={{ display: selectedClientId ? "none" : "block" }}>
      <p className="hint" style={{ marginBottom: 8 }}>
        Нагадування, відкладені на потім · {slice.length} з {items.length}.
        Натисни «повернути», щоб одразу перенести назад в активні.
      </p>
      {slice.map((r) => {
        const name = `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
        const due = new Date(r.dueAt);
        const dueStr = `${String(due.getDate()).padStart(2, "0")}.${String(
          due.getMonth() + 1,
        ).padStart(2, "0")}`;
        return (
          <div
            key={r.id}
            className="card"
            style={{ padding: 10, marginBottom: 6 }}
          >
            <div
              onClick={() => setSelectedClientId(r.client.id)}
              style={{ cursor: "pointer" }}
            >
              <div className="row">
                <span style={{ fontWeight: 600, flex: 1 }}>{name}</span>
                <span style={{ fontSize: 12 }}>
                  {KIND_SHORT[r.kind] ?? r.kind}
                </span>
              </div>
              <div className="hint" style={{ fontSize: 12, marginTop: 2 }}>
                ⏰ Повернеться {dueStr}
                {r.client.instagram ? ` · @${r.client.instagram}` : ""}
                <span style={{ float: "right" }}>›</span>
              </div>
            </div>
            <button onClick={() => unsnooze(r.id, name)} style={reopenBtn}>
              ↩️ Повернути в задачі
            </button>
          </div>
        );
      })}
      {shown < items.length && (
        <button
          onClick={() => setShown(shown + 10)}
          style={{
            width: "100%",
            padding: "10px 14px",
            marginTop: 6,
            borderRadius: 10,
            border: "1px dashed rgba(127,127,127,0.4)",
            background: "transparent",
            color: "var(--text)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ⬇ Показати ще (+{Math.min(10, items.length - shown)})
        </button>
      )}
    </div>
    </>
  );
}

// ---- Статистика (owner / developer only) ----

import {
  PeriodPicker as StatsPeriodPicker,
  periodLabel as statsPeriodLabel,
  periodToIsoRange as statsPeriodToIso,
  type PeriodValue as StatsPeriod,
} from "../components/PeriodPicker.js";

type StatRange = StatsPeriod;
const STAT_RANGE_LABEL = (r: StatRange) => statsPeriodLabel(r);

const STAT_OUTCOME_LABEL: Record<string, string> = {
  booked: "✅ Записалась",
  declined: "❌ Відмовилась",
  not_replied: "🤷 Не відповіла",
  review_positive: "👍 Гарний відгук",
  review_negative: "👎 Поганий відгук",
  review_mixed: "🟡 50/50",
  skipped: "⏭ Пропущено",
  other: "• Інше",
};

const STAT_MONTHS = [
  "січ", "лют", "бер", "кві", "тра", "чер",
  "лип", "сер", "вер", "жов", "лис", "гру",
];

function statRangeDates(r: StatRange): { from: string; to: string } {
  return statsPeriodToIso(r);
}

function fmtStatBucket(iso: string, bucket: "day" | "week" | "month"): string {
  const d = new Date(iso);
  if (bucket === "month") return STAT_MONTHS[d.getMonth()] ?? "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(
    d.getMonth() + 1,
  ).padStart(2, "0")}`;
}

interface StatsData {
  bucket: "day" | "week" | "month";
  total: number;
  reopened: number;
  byOutcome: { outcome: string; count: number }[];
  byKind: { kind: string; count: number }[];
  byAdmin: {
    adminId: string | null;
    name: string | null;
    telegramId: string | null;
    count: number;
    booked: number;
    conversionRate: number;
  }[];
  timeline: { bucket: string; count: number }[];
}

function RemindersStats() {
  const [range, setRange] = useState<StatRange>({ preset: "1m" });
  const [data, setData] = useState<StatsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    const { from, to } = statRangeDates(range);
    api
      .reminderStats(from, to)
      .then((d) => setData(d as StatsData))
      .catch((e) => setError(String(e)));
  }, [range]);

  const booked =
    data?.byOutcome.find((o) => o.outcome === "booked")?.count ?? 0;
  const conv =
    data && data.total > 0 ? Math.round((booked / data.total) * 100) : 0;

  return (
    <div>
      <StatsPeriodPicker value={range} onChange={setRange} />

      {error && (
        <div className="card">
          <p className="hint">{error}</p>
        </div>
      )}
      {!data && !error && <div className="hint">Завантаження...</div>}

      {data && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <StatCard
              label="Опрацьовано"
              value={data.total}
              color="#7c3aed"
            />
            <StatCard
              label="Записались"
              value={booked}
              hint={`${conv}% конверсія`}
              color="#10b981"
            />
            <StatCard
              label="↩️ Повернуто"
              value={data.reopened}
              hint="назад у задачі"
              color="#f59e0b"
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>
              📈 Опрацьовано задач · {STAT_RANGE_LABEL(range)}
            </h2>
            <VerticalBars
              data={data.timeline.map((t) => ({
                label: fmtStatBucket(t.bucket, data.bucket),
                value: t.count,
                display: String(t.count),
              }))}
              color="#7c3aed"
              height={200}
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>📋 Які відповіді клієнток</h2>
            <HorizontalBars
              data={data.byOutcome.map((o) => ({
                label: STAT_OUTCOME_LABEL[o.outcome] ?? o.outcome,
                value: o.count,
                display: String(o.count),
              }))}
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>🏷 Типи задач</h2>
            <HorizontalBars
              data={data.byKind.map((k) => ({
                label: KIND_SHORT[k.kind] ?? k.kind,
                value: k.count,
                display: String(k.count),
              }))}
            />
          </div>

          {data.byAdmin.length > 0 && (
            <div className="card">
              <h2 style={{ marginTop: 0 }}>👥 По адмінах · хто скільки закрив</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--hint)", fontSize: 12 }}>
                    <th style={{ padding: "4px 6px" }}>Адмінка</th>
                    <th style={{ padding: "4px 6px", textAlign: "right" }}>Закрила</th>
                    <th style={{ padding: "4px 6px", textAlign: "right" }}>Записались</th>
                    <th style={{ padding: "4px 6px", textAlign: "right" }}>Конверсія</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byAdmin.map((a) => (
                    <tr
                      key={a.adminId ?? "_unknown_"}
                      style={{ borderTop: "1px solid rgba(127,127,127,0.15)" }}
                    >
                      <td style={{ padding: "6px" }}>
                        👤 {a.name ?? `tg:${a.telegramId ?? "?"}`}
                      </td>
                      <td style={{ padding: "6px", textAlign: "right", fontWeight: 600 }}>
                        {a.count}
                      </td>
                      <td style={{ padding: "6px", textAlign: "right" }}>
                        {a.booked}
                      </td>
                      <td style={{ padding: "6px", textAlign: "right" }}>
                        {a.conversionRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Hidden salary preview — only visible inside Stats (which is
              owner+dev gated). Admin never sees this. */}
          <SalaryPreview />
        </>
      )}
    </div>
  );
}

function prettyDay(iso: string): string {
  const today = new Date();
  const [y, m, d] = iso.split("-").map(Number);
  const yyyy = y!;
  const mm = m!;
  const dd = d!;
  const isToday =
    yyyy === today.getFullYear() &&
    mm === today.getMonth() + 1 &&
    dd === today.getDate();
  if (isToday) return "Сьогодні";
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const isYesterday =
    yyyy === yesterday.getFullYear() &&
    mm === yesterday.getMonth() + 1 &&
    dd === yesterday.getDate();
  if (isYesterday) return "Вчора";
  return `${String(dd).padStart(2, "0")}.${String(mm).padStart(2, "0")}`;
}

type GeneralTask = {
  id: string;
  title: string;
  body: string | null;
  priority: "super_urgent" | "urgent" | "normal";
  status: string;
  dueAt: string | null;
  createdAt: string;
};

function ActiveReminders({ isOwner }: { isOwner: boolean }) {
  const [items, setItems] = useState<ReminderRow[] | null>(null);
  const [tasks, setTasks] = useState<GeneralTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const load = () =>
    Promise.all([api.reminders(), api.tasks()])
      .then(([rems, ts]) => {
        setItems(rems as ReminderRow[]);
        setTasks(ts as GeneralTask[]);
      })
      .catch((e) => setError(String(e)));

  useEffect(() => {
    void load();
  }, []);

  async function snooze(id: string, days: number) {
    try {
      await api.snoozeReminder(id, days);
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  async function setOutcome(
    id: string,
    outcome:
      | "not_replied"
      | "booked"
      | "declined"
      | "review_positive"
      | "review_negative",
    note?: string,
  ) {
    try {
      await api.setReminderOutcome(id, outcome, note);
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  const filtered = useMemo(() => {
    const all = items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((r) => {
      const fn = (r.client.firstName ?? "").toLowerCase();
      const ln = (r.client.lastName ?? "").toLowerCase();
      const ig = (r.client.instagram ?? "").toLowerCase();
      const ph = (r.client.phone ?? "").toLowerCase();
      return (
        fn.includes(needle) ||
        ln.includes(needle) ||
        `${fn} ${ln}`.includes(needle) ||
        ig.includes(needle) ||
        ph.includes(needle)
      );
    });
  }, [items, q]);

  const grouped = useMemo(() => {
    const m = new Map<ReminderKind, ReminderRow[]>();
    for (const r of filtered) {
      if (!m.has(r.kind)) m.set(r.kind, []);
      m.get(r.kind)!.push(r);
    }
    return m;
  }, [filtered]);

  if (error) {
    return (
      <div className="card">
        <p>Не вдалось завантажити нагадування.</p>
        <p className="hint">{error}</p>
      </div>
    );
  }
  if (!items) return <div className="hint">Завантаження...</div>;
  if (items.length === 0) {
    return (
      <div className="card">
        <p>Нагадувань нема 🎉</p>
      </div>
    );
  }

  // Client profile renders as an overlay; the list stays mounted with
  // display:none so scroll/section-open-state survive the back trip.
  return (
    <>
      {selectedClientId && (
        <ClientDetail
          id={selectedClientId}
          onBack={() => setSelectedClientId(null)}
        />
      )}
    <div style={{ display: selectedClientId ? "none" : "block" }}>
      {isOwner && (
        <>
          <ManualReminderLauncher onCreated={load} />
          <GeneralTaskLauncher onCreated={load} />
        </>
      )}
      <input
        type="text"
        placeholder="Пошук (імʼя, телефон, інстаграм)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{
          display: "block",
          width: "100%",
          padding: 10,
          marginBottom: 8,
          border: "1px solid rgba(127,127,127,0.3)",
          borderRadius: 8,
          background: "var(--bg)",
          color: "var(--text)",
          fontSize: 14,
          boxSizing: "border-box",
        }}
      />
      <p className="hint" style={{ marginBottom: 12 }}>
        {q
          ? `Знайдено: ${filtered.length} з ${items.length}`
          : `Всього ${items.length} задач. Йдемо зверху вниз — спочатку найважливіше.`}
      </p>
      {(["superurgent", "urgent", "later"] as SectionGroup[]).map((group) => {
        const groupSections = SECTIONS.filter((s) => s.group === group);
        // Manual reminders: bucket by their manualPriority into this group.
        const manualInGroup = filtered.filter(
          (r) =>
            r.kind === "manual" &&
            ((group === "superurgent" && r.manualPriority === "super_urgent") ||
              (group === "urgent" && r.manualPriority === "urgent") ||
              (group === "later" &&
                (!r.manualPriority || r.manualPriority === "normal"))),
        );
        // General tasks (not tied to any client) bucket by their `priority`.
        const tasksInGroup = tasks.filter(
          (t) =>
            (group === "superurgent" && t.priority === "super_urgent") ||
            (group === "urgent" && t.priority === "urgent") ||
            (group === "later" && (!t.priority || t.priority === "normal")),
        );
        const groupCount =
          groupSections.reduce(
            (sum, s) => sum + (grouped.get(s.kind)?.length ?? 0),
            0,
          ) +
          manualInGroup.length +
          tasksInGroup.length;
        if (groupCount === 0) return null;
        const groupStyle =
          group === "superurgent"
            ? { bg: "rgba(220,38,38,0.95)", fg: "white", label: `🚨 СУПЕР ТЕРМІНОВО · ${groupCount}` }
            : group === "urgent"
              ? { bg: "rgba(239,68,68,0.15)", fg: "#dc2626", label: `🔴 ТЕРМІНОВІ · ${groupCount}` }
              : { bg: "rgba(127,127,127,0.12)", fg: "var(--text)", label: `📋 Решта нагадувань · ${groupCount}` };
        return (
          <div key={group} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                padding: "8px 10px",
                borderRadius: 8,
                marginBottom: 8,
                background: groupStyle.bg,
                color: groupStyle.fg,
              }}
            >
              {groupStyle.label}
            </div>
            {tasksInGroup.length > 0 && (
              <GeneralTasksSection
                items={tasksInGroup}
                onRefresh={load}
                isOwner={isOwner}
              />
            )}
            {manualInGroup.length > 0 && (
              <ManualSection
                items={manualInGroup}
                onSnooze={snooze}
                onRefresh={load}
                onOpenClient={setSelectedClientId}
                isOwner={isOwner}
                defaultOpen
              />
            )}
            {groupSections.map((sec) => {
              const arr = grouped.get(sec.kind) ?? [];
              if (arr.length === 0) return null;
              return (
                <Section
                  key={sec.kind}
                  sec={sec}
                  items={arr}
                  onSnooze={snooze}
                  onOutcome={setOutcome}
                  onRefresh={load}
                  onOpenClient={setSelectedClientId}
                  isOwner={isOwner}
                  defaultOpen={group !== "later"}
                />
              );
            })}
          </div>
        );
      })}
    </div>
    </>
  );
}

function Section({
  sec,
  items,
  onSnooze,
  onOutcome,
  onRefresh,
  onOpenClient,
  isOwner,
  defaultOpen,
}: {
  sec: SectionDef;
  items: ReminderRow[];
  onSnooze: (id: string, days: number) => void;
  onOutcome: (
    id: string,
    outcome: "not_replied" | "booked" | "declined" | "review_positive" | "review_negative",
    note?: string,
  ) => void;
  onRefresh: () => void;
  onOpenClient: (clientId: string) => void;
  isOwner: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [visible, setVisible] = useState(INITIAL_VISIBLE);
  const slice = items.slice(0, visible);
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(!open)} style={sectionHeader}>
        {open ? "▾" : "▸"} {sec.emoji} {sec.title} · {items.length}
      </button>
      {open && (
        <>
          <p className="hint" style={{ marginBottom: 8, marginLeft: 4 }}>
            {sec.hint}
          </p>
          {slice.map((r) => (
            <ReminderCard
              key={r.id}
              r={r}
              hasDiscount={sec.hasDiscount}
              onSnooze={onSnooze}
              onOutcome={onOutcome}
              onRefresh={onRefresh}
              onOpenClient={onOpenClient}
              isOwner={isOwner}
            />
          ))}
          {visible < items.length && (
            <button
              onClick={() => setVisible(visible + 20)}
              style={showMoreBtn}
            >
              Показати ще ({items.length - visible})
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Той самий список, що в модерн-картці й у профілі клієнтки — див.
// referralSources.ts. Окремі копії вже раз розʼїхались: модерн-дизайн
// узагалі лишився без вибору джерела.
const REFERRAL_OPTIONS: { value: string; label: string }[] = REFERRAL_SOURCES.map(
  (s) => ({ value: s.key, label: `${s.emoji} ${s.label}` }),
);

// Softer second-attempt scripts — shown when the client ignored the first
// message and the admin snoozed the reminder once.
const NUDGE_SCRIPTS = [
  "Доброго дня, {{firstName}} 🌸 Можна хвилинку уваги? Здається, ви " +
    "пропустили моє попереднє повідомлення — буду дуже рада вашій відповіді 💛",
  "{{firstName}}, доброго дня 🌸 Не хочу бути нав'язливою — просто, схоже, " +
    "що моє повідомлення загубилось. Дайте, будь ласка, знати 💛",
];

// Скрипти для ПРОТЕРМІНОВАНИХ rebook_1m (overdue ≥ 1 день). Стандартний
// «сьогодні рівно місяць 🥳» брехливий, бо місяць уже минув.
const OVERDUE_REBOOK_SCRIPTS = [
  "Доброго дня, {{firstName}} 🌸\n\n" +
    "З Вашого останнього візиту вже пройшов місяць ✨\n" +
    "Хочемо запропонувати Вам вільні віконця, поки ще є зручні часи для запису\n\n" +
    "Поставте + і я надішлю найближчі вільні слоти для запису",
  "Доброго дня, {{firstName}} 🌸 Вже трохи більше місяця, як ми бачились — " +
    "нігтики, мабуть, просяться на оновлення?) Підкажіть, коли вам зручно — " +
    "підберу гарне віконце 💛",
  "{{firstName}}, скучили за вами 🥰 Місяць пролетів — саме час освіжити " +
    "манікюр. Надіслати вільні віконця?)",
  "Доброго дня, {{firstName}} 💅 Помітила, що ваш останній візит був більше " +
    "місяця тому. Запишемось на оновлення? Є гарні віконця найближчими днями ✨",
];

const STAGE2_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/** Stable color from a name's first letter — Люба фіолет, dev синій, etc. */
function avatarColors(name: string | null | undefined): {
  bg: string;
  fg: string;
} {
  const palette: { bg: string; fg: string }[] = [
    { bg: "#a78bfa", fg: "#3b0764" }, // violet
    { bg: "#60a5fa", fg: "#0c4a6e" }, // blue
    { bg: "#34d399", fg: "#064e3b" }, // green
    { bg: "#fb923c", fg: "#7c2d12" }, // orange
    { bg: "#f472b6", fg: "#831843" }, // pink
    { bg: "#facc15", fg: "#713f12" }, // amber
  ];
  if (!name) return palette[1]!;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length]!;
}

/** Coaching-note banner on a reopened task: avatar + author + when + body. */
function ReopenNoteBanner({
  note,
  at,
  by,
}: {
  note: string;
  at: string | null;
  by?: { telegramId: string; name: string | null } | null;
}) {
  let when = "";
  if (at) {
    const d = new Date(at);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const yesterday = new Date(today.getTime() - 86400000);
    const wasYesterday = d.toDateString() === yesterday.toDateString();
    const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes(),
    ).padStart(2, "0")}`;
    when = sameDay
      ? `сьогодні ${hhmm}`
      : wasYesterday
        ? `вчора ${hhmm}`
        : `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")} ${hhmm}`;
  }
  const authorName = by?.name?.trim() || (by ? `tg:${by.telegramId}` : "власниця");
  const initial = authorName.slice(0, 1).toUpperCase();
  const c = avatarColors(authorName);

  return (
    <div
      style={{
        background: "#fffbeb",
        border: "1px solid #fcd34d",
        borderLeft: "4px solid #f59e0b",
        borderRadius: 10,
        padding: "10px 12px",
        margin: "8px 0 10px",
        color: "#7c2d12",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: c.bg,
            color: c.fg,
            fontWeight: 800,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
        <span style={{ fontWeight: 700, color: "#92400e" }}>
          ↩️ {authorName}
        </span>
        <span style={{ fontWeight: 400, color: "#a16207", fontSize: 12 }}>
          {when ? `· ${when}` : ""}
        </span>
      </div>
      <div
        style={{
          background: "rgba(255,255,255,0.55)",
          border: "1px solid #fde68a",
          borderRadius: 8,
          padding: "8px 10px",
          whiteSpace: "pre-wrap",
          color: "#78350f",
          fontStyle: "italic",
        }}
      >
        «{note}»
      </div>
    </div>
  );
}

/** A reminder is "stage 2" once snoozed — dueAt sits well past createdAt. */
function reminderStage(r: ReminderRow): 1 | 2 {
  const diff = new Date(r.dueAt).getTime() - new Date(r.createdAt).getTime();
  return diff > STAGE2_THRESHOLD_MS ? 2 : 1;
}

function ReminderCard({
  r,
  hasDiscount,
  onSnooze,
  onOutcome,
  onRefresh,
  onOpenClient,
  isOwner,
}: {
  r: ReminderRow;
  hasDiscount: boolean;
  onSnooze: (id: string, days: number) => void;
  onOutcome: (
    id: string,
    outcome: "not_replied" | "booked" | "declined" | "review_positive" | "review_negative",
    note?: string,
  ) => void;
  onRefresh: () => void;
  onOpenClient: (clientId: string) => void;
  isOwner: boolean;
}) {
  const [variantIdx, setVariantIdx] = useState(0);
  const [discount, setDiscount] = useState("");
  const [editingScript, setEditingScript] = useState<string | null>(null);
  const [customSnoozeDate, setCustomSnoozeDate] = useState("");
  const fullName = `${r.client.firstName} ${r.client.lastName ?? ""}`.trim();
  // Stage 2 = the reminder was snoozed once (client ignored the first message).
  // Show a softer "you missed it" nudge and allow marking "не відповіла".
  const stage = reminderStage(r);
  const canNotReplied = isOwner || stage === 2;
  // Протермінований rebook_1m (dueAt у минулому ≥ 1 дня) — теплі скрипти без
  // «сьогодні рівно місяць». Stage-2 nudge має пріоритет (це snoozed-кейс).
  const overdueRebook =
    stage !== 2 &&
    r.kind === "rebook_1m" &&
    Date.now() - new Date(r.dueAt).getTime() >= STAGE2_THRESHOLD_MS;
  const scripts =
    stage === 2
      ? NUDGE_SCRIPTS
      : overdueRebook
        ? OVERDUE_REBOOK_SCRIPTS
        : r.scriptVariants;
  const rawScript = scripts[variantIdx] ?? scripts[0] ?? "";
  const renderedScript = rawScript
    .replace(/\{\{firstName\}\}/g, r.client.firstName)
    .replace(/\{\{discount\}\}/g, discount.trim() || "{{discount}}");
  const finalScript = editingScript ?? renderedScript;

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(finalScript);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="card">
      <div className="row">
        <button
          onClick={() => onOpenClient(r.client.id)}
          style={nameLinkBtn}
          title="Відкрити профіль клієнтки"
        >
          {fullName} ›
        </button>
        {r.followupReason && (
          <span style={followupBadge(r.followupReason)}>
            {r.followupReason === "first_visit"
              ? "🆕 Перший візит"
              : "💁‍♀️ Новий майстер"}
          </span>
        )}
        {stage === 2 && (
          <span style={nudgeBadge}>🔁 Повторно</span>
        )}
        {(() => {
          // Бейдж «⏰ Протермінована · N днів» — рахується від dueAt
          // (= lastVisit + 30d, точна дата коли мав спалахнути). Якщо
          // dueAt у майбутньому або сьогодні → overdue=0, без бейджа.
          if (r.kind !== "rebook_1m") return null;
          const dayMs = 24 * 60 * 60 * 1000;
          const overdueDays = Math.floor(
            (Date.now() - new Date(r.dueAt).getTime()) / dayMs,
          );
          if (overdueDays < 1) return null;
          return (
            <span className="nz-overdue-pill">
              ⏰ Протермінована · {overdueDays} дн
            </span>
          );
        })()}
      </div>

      {r.reopenNote && <ReopenNoteBanner note={r.reopenNote} at={r.reopenedAt ?? null} by={r.reopenedBy ?? null} />}

      <ContactButtons client={r.client} />
      <ContactedToggle reminderId={r.id} initial={r.contactedAt ?? null} />

      {hasDiscount && stage === 1 && (
        <div className="row" style={{ marginBottom: 6 }}>
          <span className="hint" style={{ marginRight: 6 }}>Знижка:</span>
          <input
            type="text"
            placeholder='напр. "20%" або "200 грн"'
            value={discount}
            onChange={(e) => {
              setDiscount(e.target.value);
              setEditingScript(null); // re-render with new discount
            }}
            style={discountInput}
          />
        </div>
      )}

      {stage === 2 && (
        <p className="hint" style={{ margin: "0 0 6px" }}>
          🔁 Клієнтка не відповіла на перше повідомлення — це мʼякий повторний
          нагад.
        </p>
      )}

      <textarea
        value={finalScript}
        onChange={(e) => setEditingScript(e.target.value)}
        rows={4}
        style={textareaStyle}
      />
      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
        {scripts.length > 1 && (
          <button
            onClick={() => {
              setVariantIdx((variantIdx + 1) % scripts.length);
              setEditingScript(null);
            }}
            style={smallBtn()}
          >
            Інший варіант
          </button>
        )}
        <button onClick={copyScript} style={smallBtn("var(--button)", "var(--button-text)")}>
          Скопіювати
        </button>
      </div>

      <div style={{ borderTop: "1px solid rgba(127,127,127,0.2)", margin: "12px 0 8px" }} />

      {r.kind === "ask_referral_source" && (
        <>
          <div className="hint" style={{ marginBottom: 4 }}>Звідки дізналась:</div>
          <ReferralSourcePicker reminderId={r.id} onPicked={onRefresh} />
        </>
      )}

      <MultiOutcomePicker
        showFull={r.kind !== "ask_referral_source"}
        firstName={r.client.firstName}
        canNotReplied={canNotReplied}
        onSave={async (tags, note) => {
          try {
            await api.setReminderOutcomes(r.id, tags, note);
            onRefresh();
          } catch (e) {
            handleReminderActionError(e, onRefresh);
          }
        }}
      />

      <div className="hint" style={{ margin: "10px 0 4px" }}>Інші дії:</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => onSnooze(r.id, 3)} style={smallBtn()}>На 3 дні</button>
        <button onClick={() => onSnooze(r.id, 7)} style={smallBtn()}>На тиждень</button>
        <button onClick={() => onSnooze(r.id, 14)} style={smallBtn()}>На 2 тижні</button>
        <input
          type="date"
          value={customSnoozeDate}
          min={snoozeMin()}
          max={snoozeMax()}
          onChange={(e) => setCustomSnoozeDate(e.target.value)}
          style={customDateInput}
        />
        <button
          onClick={() => {
            if (!customSnoozeDate) return;
            const target = new Date(customSnoozeDate + "T12:00:00");
            const days = Math.ceil(
              (target.getTime() - Date.now()) / (24 * 60 * 60 * 1000),
            );
            if (days < 1) {
              alert("Дата має бути в майбутньому");
              return;
            }
            if (days > 60) {
              alert("Максимум 60 днів");
              return;
            }
            onSnooze(r.id, days);
            setCustomSnoozeDate("");
          }}
          disabled={!customSnoozeDate}
          style={{
            ...smallBtn(),
            opacity: customSnoozeDate ? 1 : 0.5,
          }}
          title="Відкласти до конкретної дати"
        >
          📅 До дати
        </button>
        {isOwner && (
          <button
            onClick={async () => {
              const ok = window.confirm(
                `Пропустити «${fullName}» цього разу? Задача закриється і не зʼявиться знову — до наступного візиту клієнтки.`,
              );
              if (!ok) return;
              try {
                await api.skipReminder(r.id);
                onRefresh();
              } catch (e) {
                handleReminderActionError(e, onRefresh);
              }
            }}
            style={smallBtn("rgba(59,130,246,0.15)", "#2563eb")}
            title="Не питати цього разу — без впливу на майбутні нагадування"
          >
            ⏭ Пропустити
          </button>
        )}
        {isOwner && (
          <button
            onClick={async () => {
              const ok = window.confirm(
                `Більше ніколи не нагадувати про ${fullName}? Її поточні задачі зникнуть. Можна вернути в картці клієнта.`,
              );
              if (!ok) return;
              try {
                await api.setClientDoNotRemind(r.client.id, true);
                onRefresh();
              } catch (e) {
                handleReminderActionError(e, onRefresh);
              }
            }}
            style={smallBtn("#fde68a", "#7c2d12")}
            title="Не нагадувати про цю клієнтку"
          >
            🔕 Ігнорувати
          </button>
        )}
      </div>
    </div>
  );
}

const OUTCOME_OPTIONS: { v: string; label: string; bg?: string; fg?: string }[] = [
  { v: "booked", label: "✅ Записалась", bg: "#2ecc71", fg: "white" },
  { v: "declined", label: "❌ Відмовилась" },
  { v: "not_replied", label: "🤷 Не відповіла" },
  { v: "review_mixed", label: "🟡 50/50", bg: "#fbbf24", fg: "#7c2d12" },
  { v: "review_positive", label: "👍 Гарний відгук", bg: "#3498db", fg: "white" },
  { v: "review_negative", label: "👎 Поганий відгук", bg: "#e74c3c", fg: "white" },
];

// Outcomes that MUST have a comment before the task can be closed. Reviews
// need the client's exact wording; declined needs the reason ("дорого",
// "змінив салон", "буду пізніше" — useful signal for owner retrospect).
const REVIEW_OUTCOMES = new Set([
  "review_positive",
  "review_negative",
  "review_mixed",
  "declined",
]);

/**
 * «Написала — чекаю відповіді». Те саме, що в модерн-картці (текст і час —
 * зі спільного contacted.ts): Люба й Таня бувають у різних темах і мають
 * бачити однакове. Локальний стан — тап не перезавантажує список.
 */
function ContactedToggle({
  reminderId,
  initial,
}: {
  reminderId: string;
  initial: string | null;
}) {
  const [contactedAt, setContactedAt] = useState<string | null>(initial);
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (busy) return;
    const next = !contactedAt;
    setBusy(true);
    try {
      await api.setReminderContacted(reminderId, next);
      setContactedAt(next ? new Date().toISOString() : null);
    } catch (e) {
      alert(`Не вдалось: ${String((e as Error)?.message ?? e)}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ margin: "6px 0 8px" }}>
      <button
        disabled={busy}
        onClick={toggle}
        style={smallBtn(
          contactedAt ? "#fef3c7" : "var(--secondary-bg)",
          contactedAt ? "#92400e" : "var(--text)",
        )}
      >
        {contactedText(contactedAt)}
      </button>
    </div>
  );
}

function MultiOutcomePicker({
  showFull,
  firstName,
  canNotReplied,
  onSave,
}: {
  showFull: boolean;
  firstName: string;
  canNotReplied: boolean;
  onSave: (tags: string[], note?: string) => Promise<void>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // For ask_referral_source: hide booked + review buttons.
  let visible = showFull
    ? OUTCOME_OPTIONS
    : OUTCOME_OPTIONS.filter((o) => o.v === "declined" || o.v === "not_replied");
  // "Не відповіла" — hidden on the first attempt; appears once the reminder
  // is on its second round (or always, for the owner).
  if (!canNotReplied) visible = visible.filter((o) => o.v !== "not_replied");

  const wantsGoogleCTA = selected.has("review_positive");

  // A review outcome is selected → a comment with the client's reply is required.
  const needsComment = [...selected].some((v) => REVIEW_OUTCOMES.has(v));
  const commentMissing = needsComment && note.trim() === "";

  function toggle(v: string) {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSelected(next);
  }

  async function save() {
    if (saving || selected.size === 0 || commentMissing) return;
    setSaving(true);
    try {
      await onSave([...selected], note.trim() || undefined);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 4, fontSize: 13 }}>
        {showFull ? (
          <>
            <b>Швидкі відповіді</b>{" "}
            <span className="hint">
              — можна вибрати <b>декілька</b> (напр. 👎 поганий відгук + ✅ записалась).
            </span>
          </>
        ) : (
          <span className="hint">Швидкі відповіді:</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {visible.map((o) => {
          const on = selected.has(o.v);
          return (
            <button
              key={o.v}
              onClick={() => toggle(o.v)}
              style={smallBtn(
                on ? o.bg ?? "var(--button)" : "var(--secondary-bg)",
                on ? o.fg ?? "white" : "var(--text)",
              )}
            >
              {on ? "✓ " : ""}
              {o.label}
            </button>
          );
        })}
      </div>

      {wantsGoogleCTA && (
        <div style={{ marginTop: 8 }}>
          <GoogleReviewCTA firstName={firstName} />
        </div>
      )}

      {selected.size > 0 && (
        <textarea
          placeholder={
            needsComment
              ? selected.has("declined")
                ? "✍️ Обовʼязково: чому відмовилась? («дорого», «знайшла іншу майстерку», «буду пізніше», …)"
                : "✍️ Обовʼязково: впиши точну відповідь клієнтки — дослівно її відгук"
              : "💬 Коментар: що відповіла клієнтка (збережеться в її профіль)"
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={needsComment ? 3 : 2}
          style={{
            ...textareaStyle,
            marginTop: 8,
            border: commentMissing
              ? "1px solid #e74c3c"
              : textareaStyle.border,
          }}
        />
      )}

      {commentMissing && (
        <div style={{ color: "#e74c3c", fontSize: 12, marginTop: 4 }}>
          ⚠️ Обовʼязково впиши коментар — щоб ми знали{" "}
          {selected.has("declined")
            ? "чому клієнтка відмовилась"
            : "точну відповідь клієнтки"}
          .
        </div>
      )}

      {selected.size > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <button
            onClick={save}
            disabled={saving || commentMissing}
            style={{
              ...smallBtn("var(--button)", "var(--button-text)"),
              opacity: saving || commentMissing ? 0.5 : 1,
            }}
          >
            {saving ? "Зберігаю..." : `Зберегти (${selected.size})`}
          </button>
          <button
            onClick={() => {
              setSelected(new Set());
              setNote("");
            }}
            style={smallBtn()}
          >
            Скинути
          </button>
        </div>
      )}
    </div>
  );
}

function ReferralSourcePicker({
  reminderId,
  onPicked,
}: {
  reminderId: string;
  onPicked: () => void;
}) {
  const [otherText, setOtherText] = useState("");
  const [showOther, setShowOther] = useState(false);
  const [saving, setSaving] = useState(false);

  async function pick(value: string) {
    if (saving) return;
    setSaving(true);
    try {
      await api.setReminderReferralSource(reminderId, value);
      onPicked();
    } catch (e) {
      handleReminderActionError(e, onPicked);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {REFERRAL_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => pick(o.value)}
            style={smallBtn("var(--secondary-bg)", "var(--text)")}
            disabled={saving}
          >
            {o.label}
          </button>
        ))}
        <button
          onClick={() => setShowOther(!showOther)}
          style={smallBtn(
            showOther ? "var(--button)" : "var(--secondary-bg)",
            showOther ? "var(--button-text)" : "var(--text)",
          )}
        >
          ✏️ Інше
        </button>
      </div>
      {showOther && (
        <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
          <input
            type="text"
            placeholder="Напр. 'подруга Софія'"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            style={{
              flex: 1,
              padding: 8,
              border: "1px solid rgba(127,127,127,0.3)",
              borderRadius: 6,
              background: "var(--bg)",
              color: "var(--text)",
              fontSize: 13,
            }}
          />
          <button
            disabled={!otherText.trim() || saving}
            onClick={() => pick(otherText.trim())}
            style={smallBtn("var(--button)", "var(--button-text)")}
          >
            Зберегти
          </button>
        </div>
      )}
    </div>
  );
}

const GOOGLE_REVIEW_URL = "https://maps.app.goo.gl/kjn3S9Eou1Wdiu4s9";

function GoogleReviewCTA({ firstName }: { firstName: string }) {
  const script =
    `Дякую за теплі слова, ${firstName} 💖\n` +
    `Якщо не складно, залиш будь ласка відгук на Google — це нам дуже допомагає 🌸\n` +
    GOOGLE_REVIEW_URL;
  async function copy() {
    try {
      await navigator.clipboard.writeText(script);
    } catch {
      /* ignore */
    }
  }
  return (
    <div
      style={{
        background: "rgba(59,130,246,0.12)",
        border: "1px solid rgba(59,130,246,0.3)",
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        ⭐ Попроси Google-відгук
      </div>
      <p className="hint" style={{ marginBottom: 6, fontSize: 12 }}>
        Скопіюй і відправ клієнтці зараз — поки враження свіже.
      </p>
      <div
        style={{
          background: "var(--bg)",
          border: "1px solid rgba(127,127,127,0.25)",
          borderRadius: 6,
          padding: 8,
          fontSize: 13,
          whiteSpace: "pre-wrap",
          marginBottom: 6,
        }}
      >
        {script}
      </div>
      <button onClick={copy} style={{
        padding: "6px 12px",
        background: "#3b82f6",
        color: "white",
        border: 0,
        borderRadius: 6,
        fontSize: 13,
        cursor: "pointer",
        fontWeight: 600,
      }}>
        Скопіювати
      </button>
    </div>
  );
}

// ---- styles ----
const sectionHeader: React.CSSProperties = {
  width: "100%",
  textAlign: "left",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: 0,
  borderRadius: 8,
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  marginBottom: 6,
};

const showMoreBtn: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: "1px dashed rgba(127,127,127,0.4)",
  borderRadius: 8,
  fontSize: 13,
  cursor: "pointer",
  marginTop: 4,
  marginBottom: 6,
};

const textareaStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: 8,
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  fontFamily: "inherit",
  resize: "vertical",
  boxSizing: "border-box",
};

const nameLinkBtn: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: 0,
  margin: 0,
  font: "inherit",
  fontWeight: 700,
  fontSize: 15,
  color: "var(--link)",
  cursor: "pointer",
  textAlign: "left",
};

function followupBadge(
  reason: "first_visit" | "new_master",
): React.CSSProperties {
  const first = reason === "first_visit";
  return {
    marginLeft: 8,
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    background: first ? "rgba(16,185,129,0.18)" : "rgba(59,130,246,0.18)",
    color: first ? "#059669" : "#2563eb",
  };
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function snoozeMin(): string {
  return toIsoDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
}
function snoozeMax(): string {
  return toIsoDate(new Date(Date.now() + 60 * 24 * 60 * 60 * 1000));
}

const customDateInput: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 13,
  fontFamily: "inherit",
};

const reopenBtn: React.CSSProperties = {
  marginTop: 8,
  width: "100%",
  padding: "6px 10px",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "inherit",
};

const closerPill: React.CSSProperties = {
  display: "inline-block",
  marginLeft: 6,
  padding: "1px 7px",
  borderRadius: 999,
  background: "rgba(16,185,129,0.15)",
  color: "#047857",
  fontSize: 11,
  fontWeight: 600,
};

const assigneeBadge: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  background: "rgba(59,130,246,0.12)",
  color: "#1d4ed8",
  fontSize: 11,
  fontWeight: 600,
  marginLeft: 6,
};

const nudgeBadge: React.CSSProperties = {
  marginLeft: 8,
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
  background: "rgba(245,158,11,0.2)",
  color: "#b45309",
};

const discountInput: React.CSSProperties = {
  flex: 1,
  padding: "6px 8px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 13,
};

function smallBtn(
  bg = "var(--secondary-bg)",
  color = "var(--text)",
): React.CSSProperties {
  return {
    padding: "6px 10px",
    background: bg,
    color,
    border: "1px solid rgba(127,127,127,0.25)",
    borderRadius: 6,
    fontSize: 13,
    cursor: "pointer",
  };
}

// ---- Manual reminders (admin creates a free-text task) ----

function ManualReminderLauncher({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: "block",
          width: "100%",
          padding: "12px 14px",
          marginBottom: 10,
          borderRadius: 10,
          border: "1px dashed rgba(127,127,127,0.4)",
          background: "transparent",
          color: "var(--text)",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        ➕ Нова задача (ручна)
      </button>
    );
  }
  return (
    <ManualReminderForm
      onCancel={() => setOpen(false)}
      onSaved={() => {
        setOpen(false);
        onCreated();
      }}
    />
  );
}

function ManualReminderForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<
    { id: string; firstName: string; lastName: string | null; phone: string | null }[]
  >([]);
  const [picked, setPicked] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [priority, setPriority] =
    useState<"super_urgent" | "urgent" | "normal">("urgent");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (picked || q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .clients({ q: q.trim(), pageSize: 20 })
        .then((r) =>
          setResults(
            r.items as {
              id: string;
              firstName: string;
              lastName: string | null;
              phone: string | null;
            }[],
          ),
        )
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, picked]);

  async function save() {
    if (!picked || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createManualReminder({
        clientId: picked.id,
        title: title.trim(),
        script: script.trim() || undefined,
        priority,
      });
      onSaved();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card"
      style={{
        background:
          priority === "super_urgent"
            ? "rgba(220,38,38,0.08)"
            : priority === "urgent"
              ? "rgba(239,68,68,0.06)"
              : "transparent",
        border: "1px solid rgba(127,127,127,0.25)",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <strong>➕ Нова задача</strong>
        <span style={{ flex: 1 }} />
        <button
          onClick={onCancel}
          style={{
            background: "transparent",
            border: 0,
            fontSize: 18,
            cursor: "pointer",
            color: "var(--hint)",
          }}
        >
          ✕
        </button>
      </div>

      {!picked ? (
        <>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Кому: пошук по імені / телефону / IG"
            style={inputStyle}
            autoFocus
          />
          {results.length > 0 && (
            <div
              style={{
                marginTop: 6,
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid rgba(127,127,127,0.2)",
                borderRadius: 8,
              }}
            >
              {results.map((c) => (
                <button
                  key={c.id}
                  onClick={() =>
                    setPicked({
                      id: c.id,
                      name: `${c.firstName}${c.lastName ? " " + c.lastName : ""}`,
                    })
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    background: "transparent",
                    color: "var(--text)",
                    border: 0,
                    borderBottom: "1px solid rgba(127,127,127,0.1)",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  {c.firstName} {c.lastName ?? ""}
                  {c.phone ? (
                    <span className="hint" style={{ fontSize: 11 }}>
                      {" "}
                      · {c.phone}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
          {q.trim().length >= 2 && results.length === 0 && (
            <p className="hint" style={{ fontSize: 12, marginTop: 6 }}>
              Нікого не знайшла. Перевір написання.
            </p>
          )}
        </>
      ) : (
        <div style={{ marginBottom: 10 }}>
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(59,130,246,0.15)",
              color: "#1d4ed8",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            👤 {picked.name}
          </span>
          <button
            onClick={() => setPicked(null)}
            style={{
              marginLeft: 6,
              background: "transparent",
              border: 0,
              color: "var(--hint)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            змінити
          </button>
        </div>
      )}

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Коротко що за задача (напр. «не прийшла», «домовитись про бартер»)"
        style={{ ...inputStyle, marginTop: 8 }}
        maxLength={120}
      />
      <textarea
        value={script}
        onChange={(e) => setScript(e.target.value)}
        placeholder="Текст для клієнтки (необовʼязково — можна тільки коротку нотатку зверху)"
        rows={3}
        style={{
          ...inputStyle,
          marginTop: 8,
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />

      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 10,
          flexWrap: "wrap",
        }}
      >
        <PriorityChip
          active={priority === "super_urgent"}
          onClick={() => setPriority("super_urgent")}
          label="🚨 Супер термінова"
          colorActive="#dc2626"
        />
        <PriorityChip
          active={priority === "urgent"}
          onClick={() => setPriority("urgent")}
          label="🔴 Термінова"
          colorActive="#ef4444"
        />
        <PriorityChip
          active={priority === "normal"}
          onClick={() => setPriority("normal")}
          label="📋 Звичайна"
          colorActive="#6b7280"
        />
      </div>

      {error && (
        <p className="hint" style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
          {error}
        </p>
      )}

      <button
        onClick={save}
        disabled={busy || !picked || !title.trim()}
        style={{
          marginTop: 12,
          width: "100%",
          padding: "10px 14px",
          background:
            !picked || !title.trim() ? "rgba(127,127,127,0.2)" : "#10b981",
          color: !picked || !title.trim() ? "var(--hint)" : "#fff",
          border: 0,
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: !picked || !title.trim() ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "..." : "💾 Зберегти задачу"}
      </button>
    </div>
  );
}

function PriorityChip({
  active,
  onClick,
  label,
  colorActive,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  colorActive: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${active ? colorActive : "rgba(127,127,127,0.3)"}`,
        background: active ? colorActive : "transparent",
        color: active ? "#fff" : "var(--text)",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box",
};

function ManualSection({
  items,
  onSnooze,
  onRefresh,
  onOpenClient,
  isOwner,
  defaultOpen,
}: {
  items: ReminderRow[];
  onSnooze: (id: string, days: number) => void;
  onRefresh: () => void;
  onOpenClient: (clientId: string) => void;
  isOwner: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(!open)} style={sectionHeader}>
        {open ? "▾" : "▸"} 📝 Ручні задачі · {items.length}
      </button>
      {open && (
        <>
          {items.map((r) => (
            <ManualReminderCard
              key={r.id}
              r={r}
              onSnooze={onSnooze}
              onRefresh={onRefresh}
              onOpenClient={onOpenClient}
              isOwner={isOwner}
            />
          ))}
        </>
      )}
    </div>
  );
}

function ManualReminderCard({
  r,
  onSnooze,
  onRefresh,
  onOpenClient,
  isOwner,
}: {
  r: ReminderRow;
  onSnooze: (id: string, days: number) => void;
  onRefresh: () => void;
  onOpenClient: (clientId: string) => void;
  isOwner: boolean;
}) {
  async function del() {
    if (!confirm("Видалити цю задачу?")) return;
    try {
      await api.deleteReminder(r.id);
      onRefresh();
    } catch (e) {
      handleReminderActionError(e, onRefresh);
    }
  }
  return (
    <div
      className="card"
      style={{
        marginBottom: 8,
        borderLeft: "4px solid #6366f1",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <button
          onClick={() => onOpenClient(r.client.id)}
          style={nameLinkBtn}
          title="Відкрити профіль клієнтки"
        >
          {r.client.firstName} {r.client.lastName ?? ""} ›
        </button>
        <span style={{ flex: 1 }} />
        {isOwner && (
          <button
            onClick={del}
            title="Видалити задачу"
            style={{
              background: "transparent",
              border: 0,
              color: "#dc2626",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            🗑
          </button>
        )}
      </div>

      {r.manualTitle && (
        <p style={{ margin: "4px 0 6px", fontWeight: 600 }}>{r.manualTitle}</p>
      )}

      {r.reopenNote && <ReopenNoteBanner note={r.reopenNote} at={r.reopenedAt ?? null} by={r.reopenedBy ?? null} />}

      {r.scriptVariants[0] && (
        <div
          style={{
            background: "rgba(127,127,127,0.08)",
            padding: 10,
            borderRadius: 8,
            fontSize: 14,
            whiteSpace: "pre-wrap",
            marginBottom: 8,
          }}
        >
          {r.scriptVariants[0]}
        </div>
      )}

      <ContactButtons client={r.client} />
      <ContactedToggle reminderId={r.id} initial={r.contactedAt ?? null} />

      <div style={{ marginTop: 10 }}>
        <MultiOutcomePicker
          showFull
          firstName={r.client.firstName}
          canNotReplied
          onSave={async (tags, note) => {
            try {
              await api.setReminderOutcomes(r.id, tags, note);
              onRefresh();
            } catch (e) {
              handleReminderActionError(e, onRefresh);
            }
          }}
        />
      </div>

      <div className="hint" style={{ margin: "10px 0 4px" }}>Інші дії:</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button onClick={() => onSnooze(r.id, 1)} style={smallBtn()}>
          💤 +1 день
        </button>
        <button onClick={() => onSnooze(r.id, 7)} style={smallBtn()}>
          💤 +тиждень
        </button>
      </div>

      {!isOwner ? null : (
        <p className="hint" style={{ fontSize: 11, marginTop: 6 }}>
          ID: {r.id}
        </p>
      )}
    </div>
  );
}


// ---- General tasks (free-floating, not tied to a client) ----

function GeneralTaskLauncher({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: "block",
          width: "100%",
          padding: "12px 14px",
          marginBottom: 10,
          borderRadius: 10,
          border: "1px dashed rgba(127,127,127,0.4)",
          background: "transparent",
          color: "var(--text)",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        🏢 Загальна задача (без клієнта)
      </button>
    );
  }
  return (
    <GeneralTaskForm
      onCancel={() => setOpen(false)}
      onSaved={() => {
        setOpen(false);
        onCreated();
      }}
    />
  );
}

function GeneralTaskForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] =
    useState<"super_urgent" | "urgent" | "normal">("normal");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createTask({
        title: title.trim(),
        body: body.trim() || undefined,
        priority,
        dueAt: dueAt || undefined,
      });
      onSaved();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card"
      style={{
        background:
          priority === "super_urgent"
            ? "rgba(220,38,38,0.08)"
            : priority === "urgent"
              ? "rgba(239,68,68,0.06)"
              : "transparent",
        border: "1px solid rgba(127,127,127,0.25)",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <strong>🏢 Загальна задача</strong>
        <span style={{ flex: 1 }} />
        <button
          onClick={onCancel}
          style={{
            background: "transparent",
            border: 0,
            fontSize: 18,
            cursor: "pointer",
            color: "var(--hint)",
          }}
        >
          ✕
        </button>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Коротко що треба зробити («замовити фрези», «знайти постачальника»)"
        style={taskInputStyle}
        maxLength={200}
        autoFocus
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Деталі (необовʼязково)"
        rows={3}
        style={{
          ...taskInputStyle,
          marginTop: 8,
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />
      <input
        type="date"
        value={dueAt}
        onChange={(e) => setDueAt(e.target.value)}
        style={{ ...taskInputStyle, marginTop: 8 }}
        placeholder="Дедлайн (необов..)"
      />
      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 10,
          flexWrap: "wrap",
        }}
      >
        <PriorityChip
          active={priority === "super_urgent"}
          onClick={() => setPriority("super_urgent")}
          label="🚨 Супер термінова"
          colorActive="#dc2626"
        />
        <PriorityChip
          active={priority === "urgent"}
          onClick={() => setPriority("urgent")}
          label="🔴 Термінова"
          colorActive="#ef4444"
        />
        <PriorityChip
          active={priority === "normal"}
          onClick={() => setPriority("normal")}
          label="📋 Звичайна"
          colorActive="#6b7280"
        />
      </div>
      {error && (
        <p className="hint" style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
          {error}
        </p>
      )}
      <button
        onClick={save}
        disabled={busy || !title.trim()}
        style={{
          marginTop: 12,
          width: "100%",
          padding: "10px 14px",
          background: !title.trim() ? "rgba(127,127,127,0.2)" : "#10b981",
          color: !title.trim() ? "var(--hint)" : "#fff",
          border: 0,
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          cursor: !title.trim() ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "..." : "💾 Зберегти задачу"}
      </button>
    </div>
  );
}

const taskInputStyle: CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box",
};

function GeneralTasksSection({
  items,
  onRefresh,
  isOwner,
}: {
  items: GeneralTask[];
  onRefresh: () => void;
  isOwner: boolean;
}) {
  const [open, setOpen] = useState(true);
  async function done(id: string) {
    try {
      await api.markTaskDone(id);
      onRefresh();
    } catch (e) {
      handleReminderActionError(e, onRefresh);
    }
  }
  async function del(id: string) {
    if (!confirm("Видалити задачу?")) return;
    try {
      await api.deleteTask(id);
      onRefresh();
    } catch (e) {
      handleReminderActionError(e, onRefresh);
    }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(!open)} style={sectionHeader}>
        {open ? "▾" : "▸"} 🏢 Загальні задачі · {items.length}
      </button>
      {open && (
        <>
          {items.map((t) => (
            <div
              key={t.id}
              className="card"
              style={{
                marginBottom: 8,
                borderLeft: "4px solid #0ea5e9",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                <strong style={{ flex: 1, fontSize: 15 }}>{t.title}</strong>
                {isOwner && (
                  <button
                    onClick={() => del(t.id)}
                    style={{
                      background: "transparent",
                      border: 0,
                      color: "#dc2626",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    🗑
                  </button>
                )}
              </div>
              {t.body && (
                <p style={{ margin: "4px 0", fontSize: 13, whiteSpace: "pre-wrap" }}>
                  {t.body}
                </p>
              )}
              {t.dueAt && (
                <p className="hint" style={{ fontSize: 11, margin: "4px 0" }}>
                  📅 До {new Date(t.dueAt).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" })}
                </p>
              )}
              <button
                onClick={() => done(t.id)}
                style={smallBtn("rgba(16,185,129,0.15)", "#047857")}
              >
                ✅ Готово
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}


// ---- Hidden salary preview (owner + dev only — gated by RemindersStats) ----

// Дефолт = поточний зарплатний цикл 23→23, іменований за днем виплати
// (сьогодні ≥23 → наступний місяць). Синхронно з бекендом kyivPayCycleBounds.
function thisMonth(): string {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Kyiv",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date())
    .split("-")
    .map(Number);
  let py = y;
  let pm = m;
  if (d >= 23) {
    if (m === 12) {
      py = y + 1;
      pm = 1;
    } else {
      pm = m + 1;
    }
  }
  return `${py}-${String(pm).padStart(2, "0")}`;
}

function SalaryPreview() {
  const [month, setMonth] = useState(thisMonth());
  const [adminId, setAdminId] = useState<string | undefined>();
  const [data, setData] = useState<any | null>(null);
  const [summary, setSummary] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .adminSalary({ month, adminId })
      .then((d) => setData(d as any))
      .catch((e) => setError(String(e)));
  }, [month, adminId]);

  useEffect(() => {
    setSummary(null);
    api
      .adminSalarySummary({ month })
      .then((d) => setSummary(d as any))
      .catch(() => setSummary(null));
  }, [month]);

  if (error)
    return (
      <div className="card" style={{ background: "rgba(220,38,38,0.05)" }}>
        <p className="hint">💰 Зарплата: {error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="card" style={{ background: "rgba(127,127,127,0.05)" }}>
        <p className="hint">💰 Завантаження зарплатної моделі...</p>
      </div>
    );

  const pay = data.finalPay as number;
  const payColor =
    pay >= 8000 ? "#10b981" : pay >= 5000 ? "#f59e0b" : "#dc2626";

  // Choose previous / next month
  function shiftMonth(delta: number) {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y!, m! - 1 + delta, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        background: "rgba(168,85,247,0.06)",
        border: "1px dashed rgba(168,85,247,0.4)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong style={{ fontSize: 16 }}>
          💰 Зарплатна модель (тест — приховано від адміна)
        </strong>
      </div>

      <p className="hint" style={{ fontSize: 12, margin: "6px 0 12px" }}>
        База 4 000 грн (гарантовано) + 30 грн × тип × outcome × вчасність
        за кожну закриту задачу. Конверсія ≥40% → +1k. Адмін цього блоку
        не бачить.
      </p>

      {data.admins.length > 1 && (
        <select
          value={adminId ?? data.admin.id}
          onChange={(e) => setAdminId(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid rgba(127,127,127,0.3)",
            background: "var(--bg)",
            color: "var(--text)",
            fontSize: 14,
            marginBottom: 8,
            boxSizing: "border-box",
          }}
        >
          {data.admins.map((a: any) => (
            <option key={a.id} value={a.id}>
              {a.name || a.telegramId}
            </option>
          ))}
        </select>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => shiftMonth(-1)} style={smallBtn()}>
          ‹ {prevMonth(month)}
        </button>
        <span
          style={{
            flex: 1,
            textAlign: "center",
            padding: "6px 10px",
            fontWeight: 600,
          }}
        >
          📅 {month}
        </span>
        <button onClick={() => shiftMonth(1)} style={smallBtn()}>
          {nextMonth(month)} ›
        </button>
      </div>

      {summary && summary.rows.length > 1 && (
        <div
          style={{
            background: "var(--bg)",
            borderRadius: 12,
            padding: "8px 10px",
            marginBottom: 12,
          }}
        >
          <div className="hint" style={{ fontSize: 11, marginBottom: 4 }}>
            👥 Хто скільки заробив цього місяця:
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--hint)", fontSize: 11 }}>
                <th style={{ padding: "3px 4px" }}>Адмінка</th>
                <th style={{ padding: "3px 4px", textAlign: "right" }}>Закрила</th>
                <th style={{ padding: "3px 4px", textAlign: "right" }}>Конверсія</th>
                <th style={{ padding: "3px 4px", textAlign: "right" }}>ЗП</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r: any) => (
                <tr
                  key={r.adminId}
                  onClick={() => setAdminId(r.adminId)}
                  style={{
                    borderTop: "1px solid rgba(127,127,127,0.15)",
                    cursor: "pointer",
                    background:
                      (adminId ?? data?.admin?.id) === r.adminId
                        ? "rgba(168,85,247,0.1)"
                        : "transparent",
                  }}
                >
                  <td style={{ padding: "5px 4px", fontWeight: 600 }}>
                    👤 {r.name ?? `tg:${r.telegramId}`}
                  </td>
                  <td style={{ padding: "5px 4px", textAlign: "right" }}>
                    {r.totalClosed}
                  </td>
                  <td style={{ padding: "5px 4px", textAlign: "right" }}>
                    {r.conversionRate}%
                  </td>
                  <td
                    style={{
                      padding: "5px 4px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: r.finalPay >= 5000 ? "#10b981" : "#dc2626",
                    }}
                  >
                    {r.finalPay} ₴
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          textAlign: "center",
          padding: "18px 0",
          marginBottom: 12,
          background: "var(--bg)",
          borderRadius: 12,
        }}
      >
        <div className="hint" style={{ fontSize: 11 }}>
          Підсумок ЗП
        </div>
        <div
          style={{
            fontSize: 36,
            fontWeight: 800,
            color: payColor,
            marginTop: 4,
          }}
        >
          {data.finalPay.toLocaleString("uk-UA")} грн
        </div>
        <div className="hint" style={{ fontSize: 11, marginTop: 4 }}>
          {data.baseSalary.toLocaleString("uk-UA")} грн база
          {data.earnedFromTasks > 0 &&
            ` + ${data.earnedFromTasks.toLocaleString("uk-UA")} грн з задач`}
          {data.conversionBonus > 0 &&
            ` + ${data.conversionBonus.toLocaleString("uk-UA")} грн бонус`}
          {(data.tonusPenalty > 0 || data.ownerPenalty > 0) &&
            ` − ${(data.tonusPenalty + data.ownerPenalty).toLocaleString("uk-UA")} грн штрафи`}
        </div>
      </div>

      <table style={{ width: "100%", fontSize: 13 }}>
        <tbody>
          <Row
            label="🛡 База (гарантовано)"
            v={`${data.baseSalary.toLocaleString("uk-UA")} грн`}
            color="#0ea5e9"
          />
          <Row label="🎯 Закритих задач" v={data.totalClosed} />
          <Row
            label="💼 Заробила з задач"
            v={`${data.earnedFromTasks.toLocaleString("uk-UA")} грн`}
          />
          <Row
            label="✅ Конверсія в booked"
            v={`${data.conversionRate}% (${data.bookedCount} з ${data.totalClosed})`}
          />
          {data.conversionBonus > 0 && (
            <Row
              label="🎁 Бонус за конверсію ≥40%"
              v={`+${data.conversionBonus} грн`}
              color="#10b981"
            />
          )}
          {data.reopenedCount > 0 && (
            <Row
              label={`↩️ Повернуто назад × ${data.reopenedCount}`}
              v="не оплачено"
              color="#f59e0b"
            />
          )}
          {data.supersededCount > 0 && (
            <Row
              label={`👥 Інша адмінка переробила × ${data.supersededCount}`}
              v="не оплачено"
              color="#f59e0b"
            />
          )}
          {data.tonusWarns > 0 && (
            <Row
              label={`⚠️ Tonus warns × ${data.tonusWarns}`}
              v={`−${data.tonusPenalty} грн`}
              color="#dc2626"
            />
          )}
          {data.ownerPings > 0 && (
            <Row
              label={`🚨 Owner pings × ${data.ownerPings}`}
              v={`−${data.ownerPenalty} грн`}
              color="#dc2626"
            />
          )}
        </tbody>
      </table>

      <details style={{ marginTop: 14 }}>
        <summary
          className="hint"
          style={{ fontSize: 12, cursor: "pointer", fontWeight: 600 }}
        >
          📊 Деталі: по типу задач
        </summary>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          {data.byKind.map((k: any) => (
            <div key={k.kind} style={breakdownRow}>
              <span>{KIND_SHORT[k.kind] ?? k.kind}</span>
              <span className="spacer" />
              <span style={{ opacity: 0.7 }}>{k.count} шт</span>
              <span style={{ minWidth: 64, textAlign: "right", fontWeight: 600 }}>
                {k.earned} грн
              </span>
            </div>
          ))}
        </div>
      </details>

      <details style={{ marginTop: 6 }}>
        <summary
          className="hint"
          style={{ fontSize: 12, cursor: "pointer", fontWeight: 600 }}
        >
          ⏰ Деталі: по вчасності
        </summary>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          {data.byTimeliness.map((b: any) => (
            <div key={b.label} style={breakdownRow}>
              <span>{b.label}</span>
              <span className="hint" style={{ marginLeft: 6 }}>
                × {b.weight}
              </span>
              <span className="spacer" />
              <span style={{ opacity: 0.7 }}>{b.count} шт</span>
              <span style={{ minWidth: 64, textAlign: "right", fontWeight: 600 }}>
                {b.earned} грн
              </span>
            </div>
          ))}
        </div>
      </details>

      <details style={{ marginTop: 6 }}>
        <summary
          className="hint"
          style={{ fontSize: 12, cursor: "pointer", fontWeight: 600 }}
        >
          📅 Денний графік ({data.daily.length} днів)
        </summary>
        <div style={{ marginTop: 6, fontSize: 12, maxHeight: 240, overflowY: "auto" }}>
          {data.daily.map((d: any) => (
            <div key={d.day} style={breakdownRow}>
              <span>{d.day.slice(5)}</span>
              <span className="spacer" />
              <span style={{ opacity: 0.7 }}>{d.count} задач</span>
              <span style={{ minWidth: 64, textAlign: "right", fontWeight: 600 }}>
                {d.earned} грн
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function Row({
  label,
  v,
  color,
}: {
  label: string;
  v: string | number;
  color?: string;
}) {
  return (
    <tr>
      <td style={{ padding: "4px 0", color: "var(--text)" }}>{label}</td>
      <td
        style={{
          padding: "4px 0",
          textAlign: "right",
          fontWeight: 700,
          color: color ?? "var(--text)",
        }}
      >
        {v}
      </td>
    </tr>
  );
}

const breakdownRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "3px 0",
  borderBottom: "1px solid rgba(127,127,127,0.1)",
};

function prevMonth(m: string): string {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y!, mo! - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function nextMonth(m: string): string {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y!, mo!, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
